'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/linear-extra'

const TensorRanks = dynamic(() => import('../_components/widgets/TensorWidgets').then(m => m.TensorRanks), { ssr: false })
const ReshapeViz = dynamic(() => import('../_components/widgets/TensorWidgets').then(m => m.ReshapeViz), { ssr: false })
const BroadcastViz = dynamic(() => import('../_components/widgets/TensorWidgets').then(m => m.BroadcastViz), { ssr: false })

export default function TensorsAndBroadcasting() {
  return (
    <Lesson>
      <Step>
        <H>Beyond the grid</H>
        <P>
          A number, a list of numbers, a grid of numbers… what comes next? A stack of grids. Then a row of stacks. The general
          word for all of these is <strong>tensor</strong>. It’s the “Tensor” in TensorFlow, and the thing every AI chip is
          built to crunch.
        </P>
        <P>
          A tensor’s <strong>rank</strong> is how many indices you need to point at one number inside it. Its{' '}
          <strong>shape</strong> lists how far each index goes.
        </P>
        <Figure n="3.8" wide caption="Tensors of rank 0 to 4. Hover any cell to see the indices that point at it — one index per axis.">
          <TensorRanks />
        </Figure>
      </Step>

      <Step gate={['nq-count', 'q-shape']}>
        <H>Reading shapes</H>
        <NumberQuiz
          id="nq-count"
          question={
            <>
              A batch of 32 colour photos, each 64 × 64 pixels with 3 colour channels, has shape <C>[32, 3, 64, 64]</C>. How many
              numbers is that?
            </>
          }
          answer={393216}
          explain={<>32 × 3 × 64 × 64 = 393 216. Multiply all the sizes together. Tensors get big fast.</>}
          hint={<>The number of entries is the product of every size in the shape.</>}
        />
        <Quiz
          id="q-shape"
          question="A language model reads a batch of 8 sentences, each 128 tokens long, and turns every token into 512 numbers. What shape is that tensor?"
          options={[
            { label: '[8, 128, 512]', correct: true },
            { label: '[8, 512]', why: 'That would be one vector per sentence — but every token gets its own 512 numbers.' },
            { label: '[128, 512]', why: 'That’s one sentence. There are 8 of them in the batch.' },
          ]}
          explain="[batch, sequence length, embedding size] is the most common shape inside every transformer. You’ll build one in Module 9."
        />
      </Step>

      <Step>
        <H>All tensors are secretly flat</H>
        <P>
          Computer memory is one long row of slots. So a tensor is really stored as a flat list, with its shape as a recipe for
          reading it. For a matrix with <M>{'c'}</M> columns, entry <M>{'(i, j)'}</M> lives at position:
        </P>
        <MB>{'\\text{position} = i \\times c + j'}</MB>
        <P>
          That means a <strong>reshape</strong> is almost free: same numbers, same order, new recipe. A{' '}
          <strong>transpose</strong> is different. It really moves numbers around.
        </P>
        <Figure n="3.9" wide caption="Twelve numbers, coloured by their position in memory. Reshape keeps the colours in reading order; transpose scrambles them.">
          <ReshapeViz />
        </Figure>
        <Exercise id="tensor-intro" title="Meet the course Tensor" {...EX.tensorIntro} />
      </Step>

      <Step gate="flat-index">
        <H>Write the recipe</H>
        <Exercise
          id="flat-index"
          title="flatIndex, reshape, flatten"
          {...EX.flatIndex}
          hints={[
            <>
              <C>return i * cols + j</C>
            </>,
            <>
              Row <C>i</C> of the reshaped matrix is <C>{'flat.slice(i * cols, (i + 1) * cols)'}</C>. Throw with{' '}
              <C>{'throw new Error("…")'}</C>.
            </>,
            <>
              flatten: <C>{'for (const row of M) out.push(...row)'}</C>
            </>,
          ]}
        />
      </Step>

      <Step gate="q-broadcast">
        <H>Broadcasting: stretching to fit</H>
        <P>
          To add something to every row of a matrix, you could copy it once per row. <strong>Broadcasting</strong> does that
          copying for you (and skips the actual copying, so it costs nothing). It’s how a bias gets added to a whole batch in one
          step.
        </P>
        <Figure n="3.10" wide caption="Adding to a 3 × 4 matrix. A row, a column or a single number gets stretched to 3 × 4. A row of the wrong length can’t be.">
          <BroadcastViz />
        </Figure>
        <Quiz
          id="q-broadcast"
          question="Which of these can be added to a 5 × 3 matrix by broadcasting?"
          options={[
            { label: 'A 5 × 1 column', correct: true },
            { label: 'A 1 × 5 row', why: 'The matrix has 3 columns, the row has 5 — and neither is 1.' },
            { label: 'A 3 × 5 matrix', why: 'Both axes mismatch. You’d need to transpose it first.' },
          ]}
        />
      </Step>

      <Step gate="axis-sums">
        <H>Squeezing along an axis</H>
        <P>
          The opposite of stretching is <strong>reducing</strong>: summing or averaging along one axis, which makes that axis
          disappear. Average down the rows of a <C>[examples, features]</C> table and you get one mean per feature. Subtract it
          (broadcasting!) and every feature is <strong>centred</strong> on zero. Divide by each feature’s spread and they’re{' '}
          <strong>standardised</strong>: every feature becomes equally important, whatever its units.
        </P>
        <Exercise
          id="axis-sums"
          title="columnMeans, center, standardize"
          {...EX.axisSums}
          hints={[
            <>
              Keep one running sum per column: <C>{'const sums = new Array(cols).fill(0)'}</C>, then divide each by{' '}
              <C>X.length</C>.
            </>,
            <>
              center: <C>{'X.map(row => row.map((v, j) => v - m[j]))'}</C>
            </>,
            <>
              standardize: the column spreads are <C>{'columnMeans(C.map(row => row.map(v => v * v))).map(Math.sqrt)'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          Standardising inputs is often the difference between a model that trains in seconds and one that doesn’t train at all.
          You’ll see why in Module 6, where house sizes (hundreds) and bedroom counts (single digits) share one model.
        </Callout>
      </Step>
    </Lesson>
  )
}
